import {InputElement} from "./input-element.js";

/**
 * Class representing a text input element in a form.
 * Extends the InputElement class and provides specific functionalities for text input fields.
 */
export class TextElement extends InputElement {
    placeholder;
    maxLength;
    validation;
    validationErrorMessage;

    /**
     * Constructor for creating an instance of the class.
     *
     * @param {Object} formData - The data object containing form-related information.
     * @param {HTMLElement} parentElement - The parent HTML element where the instance will be rendered or attached.
     * @param {Array} collection - An array of items to be associated with the instance.
     * @return {void} Does not return anything.
     */
    constructor(formData, parentElement, collection) {
        super(formData, parentElement, collection);
    }

    /**
     * Retrieves the input type for a specific form control or input field.
     *
     * @return {string} The input type, which is "text".
     */
    inputType() {
        return "text";
    }

    valueChange(event) {
        const changed = super.valueChange(event);

        if (changed) {
            this.validateInput();
        }

        return changed;
    }

    /**
     * Handles the change event for the current object. Validates input data and calls the parent class's changeEvent method with the provided data.
     *
     * @param {Object} data - The data associated with the change event.
     * @return {void} This method does not return a value.
     */
    changeEvent(data) {
        this.validateInput();
        super.changeEvent(data);
    }

    /**
     * Validates the current input based on the specified validation type and data value.
     * If no validation type or data value is provided, the input is considered valid.
     * If a validation type is set but not implemented in the validation map, an error message is returned.
     * If the input fails the validation, an appropriate error message is set.
     *
     * @return {boolean|string} Returns true if the input is valid or validation is not required,
     *                          or returns an error message if validation fails.
     */
    validateInput() {
        if (this.validation == null || this.dataValue == null) {
            return true;
        }

        const validationMap = {
            "phone": /^\+?[0-9().\-\s]+$/,
            "email": /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
        }

        const validationRegEx = validationMap[this.validation];
        if (validationRegEx == null) {
            return this.setErrorMessage(`Validation ${this.validation} not implemented yet`)
        }

        if (validationRegEx.test(this.dataValue)) {
            return super.validateInput();
        }

        return this.setErrorMessage(`Invalid ${this.validationErrorMessage}`);
    }

    /**
     * Configures the provided component with additional attributes such as placeholder and maxLength
     * if they are defined and valid.
     *
     * @param {Object} component - The component to which the attributes will be applied.
     * @return {void} This method does not return a value.
     */
    attributes(component) {
        super.attributes(component);

        if (this.placeholder != null) {
            component.placeholder = this.placeholder;
        }

        if (this.maxLength != null && Number.isInteger(this.maxLength)) {
            component.maxLength = this.maxLength;
        }
    }
}
